import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { gsap } from "@/animations/gsap";
import { createProjectsShowcase } from "@/animations/projects";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useIsDesktop } from "@/hooks/useDevice";
import { projects } from "@/data/projects";
import { ProjectVisual } from "./ProjectVisual";

export function Projects() {
  const sectionRef = useRef<HTMLElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();
  const pinned = isDesktop && !reduced;

  useEffect(() => {
    if (!pinned) return undefined;
    const ctx = gsap.context(() => {
      createProjectsShowcase({
        section: sectionRef.current!,
        pin: pinRef.current!,
        progressLine: progressRef.current!,
        onUpdate: (i) => setActive((cur) => (cur === i ? cur : i)),
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [pinned]);

  return (
    <section id="work" ref={sectionRef} className="relative" data-testid="projects-section">
      <div className="container-x pt-24 md:pt-32">
        <p className="eyebrow">05 — SELECTED WORK</p>
      </div>

      {pinned ? (
        <div ref={pinRef} className="container-x flex h-screen flex-col justify-center" data-testid="projects-showcase-pinned">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-2xl font-semibold uppercase tracking-tight md:text-3xl">PROJECTS THAT SHIPPED</h2>
            <p className="font-mono text-xs tracking-[0.3em] text-muted" data-testid="projects-counter">
              {String(active + 1).padStart(2, "0")} / {String(projects.length).padStart(2, "0")}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-12 items-center gap-10">
            <div className="relative col-span-7 h-[54vh] border border-line bg-surface/40">
              {projects.map((p, i) => (
                <div key={p.id} className={`project-visual absolute inset-0 p-8 ${i > 0 ? "opacity-0" : ""}`} data-testid={`project-visual-${p.id}`}>
                  <ProjectVisual project={p} />
                </div>
              ))}
            </div>
            <div className="relative col-span-5 h-[54vh]">
              {projects.map((p, i) => (
                <article key={p.id} className={`project-meta absolute inset-0 flex flex-col justify-center ${i > 0 ? "opacity-0" : ""}`} data-testid={`project-meta-${p.id}`}>
                  <p className="font-mono text-xs tracking-[0.3em] text-accent">PROJECT {p.index}</p>
                  <h3 className="font-display mt-5 text-3xl font-semibold leading-tight tracking-tight md:text-4xl">{p.title}</h3>
                  <p className="mt-6 text-sm leading-relaxed text-muted">{p.description}</p>
                  <ul className="mt-8 flex flex-wrap gap-2">
                    {p.tech.map((t) => (
                      <li key={t} className="border border-line px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] text-foreground/80">
                        {t.toUpperCase()}
                      </li>
                    ))}
                  </ul>
                  {p.features ? (
                    <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-muted">{p.features.join(" · ").toUpperCase()}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
          <div className="mt-12 h-px w-full bg-line">
            <div ref={progressRef} className="h-full w-full" style={{ backgroundColor: "var(--accent)", transform: "scaleX(0)", transformOrigin: "left" }} data-testid="projects-progress" />
          </div>
        </div>
      ) : (
        <div className="container-x grid gap-16 pb-4 pt-14">
          {projects.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`project-card-${p.id}`}
            >
              <div className="relative h-[52vh] min-h-[360px] border border-line bg-surface/40" data-testid={`project-visual-${p.id}`}>
                <ProjectVisual project={p} />
              </div>
              <div className="mt-8">
                <p className="font-mono text-xs tracking-[0.3em] text-accent">
                  PROJECT {p.index} — {p.tagline}
                </p>
                <h3 className="font-display mt-4 text-2xl font-semibold tracking-tight md:text-3xl">{p.title}</h3>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted">{p.description}</p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {p.tech.map((t) => (
                    <li key={t} className="border border-line px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] text-foreground/80">
                      {t.toUpperCase()}
                    </li>
                  ))}
                </ul>
                {p.features ? (
                  <p className="mt-5 font-mono text-[10px] leading-relaxed tracking-[0.15em] text-muted">{p.features.join(" · ").toUpperCase()}</p>
                ) : null}
              </div>
            </motion.article>
          ))}
        </div>
      )}
    </section>
  );
}
